import { createTheme, PaletteMode, Theme, alpha } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import {
  accent,
  controlHeight,
  cssEase,
  duration,
  ease,
  fontFamily,
  fontFamilyMono,
  grey,
  ms,
  radius,
  semantic,
  shadowDark,
  shadowLight,
  spacingUnit,
  teal,
  tracking,
} from './tokens';

export * from './tokens';

/**
 * Builds the full MUI theme from the design tokens. `App.tsx` calls this once
 * per colour mode — no styling decisions live outside this file and tokens.ts.
 */
export const createAppTheme = (mode: PaletteMode): Theme => {
  const isLight = mode === 'light';
  const shadow = isLight ? shadowLight : shadowDark;
  const sem = semantic[mode];

  const surface = {
    canvas: isLight ? grey[50] : grey[950],
    paper: isLight ? grey[0] : grey[900],
    raised: isLight ? grey[0] : grey[800],
    sunken: isLight ? grey[100] : grey[950],
    border: isLight ? grey[200] : grey[800],
    borderStrong: isLight ? grey[300] : grey[700],
  };

  const text = {
    primary: isLight ? grey[900] : grey[50],
    secondary: isLight ? grey[500] : grey[400],
    disabled: isLight ? grey[400] : grey[600],
  };

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        light: accent[300],
        // 600, not 500, in light mode: white text on accent.500 is 3.53:1, under
        // the 4.5:1 AA minimum; on 600 it is 4.53:1. Charts keep 500 (marks
        // need 3:1, and their pair was validated on it).
        main: isLight ? accent[600] : accent[400],
        dark: accent[700],
        // Dark mode puts dark text on the lighter accent.
        contrastText: isLight ? grey[0] : accent[900],
      },
      secondary: {
        light: teal[300],
        main: isLight ? teal[500] : teal[400],
        dark: teal[600],
        contrastText: '#FFFFFF',
      },
      success: sem.success,
      warning: sem.warning,
      error: sem.danger,
      info: sem.info,
      background: { default: surface.canvas, paper: surface.paper },
      text,
      divider: surface.border,
      grey,
    },

    shape: { borderRadius: radius.md },

    spacing: spacingUnit,

    // MUI wants exactly 25 entries; ours ramp through the token scale.
    shadows: [
      'none',
      shadow.xs,
      shadow.xs,
      shadow.sm,
      shadow.sm,
      shadow.md,
      shadow.md,
      shadow.md,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow['2xl'],
      shadow['2xl'],
      shadow['2xl'],
      shadow['2xl'],
      shadow['2xl'],
      shadow['2xl'],
      shadow['2xl'],
      shadow['2xl'],
    ] as Theme['shadows'],

    typography: {
      fontFamily,
      // Display: hero headlines. Tight tracking keeps large type from
      // reading loose. Sizes are fluid so mobile never overflows.
      h1: {
        fontSize: 'clamp(2.5rem, 1.6rem + 3.8vw, 4.25rem)',
        fontWeight: 700,
        lineHeight: 1.04,
        letterSpacing: tracking.display,
      },
      h2: {
        fontSize: 'clamp(2rem, 1.4rem + 2.4vw, 3rem)',
        fontWeight: 700,
        lineHeight: 1.12,
        letterSpacing: tracking.display,
      },
      h3: {
        fontSize: 'clamp(1.5rem, 1.2rem + 1.2vw, 2.125rem)',
        fontWeight: 650,
        lineHeight: 1.2,
        letterSpacing: tracking.heading,
      },
      h4: { fontSize: '1.5rem', fontWeight: 650, lineHeight: 1.28, letterSpacing: tracking.heading },
      h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: tracking.heading },
      h6: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.45, letterSpacing: tracking.heading },
      subtitle1: { fontSize: '1.0625rem', fontWeight: 500, lineHeight: 1.55 },
      subtitle2: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.5 },
      body1: { fontSize: '1rem', lineHeight: 1.6, letterSpacing: tracking.body },
      body2: { fontSize: '0.9375rem', lineHeight: 1.6, letterSpacing: tracking.body },
      button: { fontSize: '0.9375rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
      caption: { fontSize: '0.8125rem', lineHeight: 1.5, color: text.secondary },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 650,
        letterSpacing: tracking.overline,
        textTransform: 'uppercase',
      },
    },

    transitions: {
      duration: {
        shortest: 120,
        shorter: 160,
        short: 200,
        standard: 280,
        complex: 380,
        enteringScreen: 240,
        leavingScreen: 180,
      },
      easing: {
        easeOut: cssEase(ease.out),
        easeIn: cssEase(ease.inOut),
        easeInOut: cssEase(ease.inOut),
        sharp: cssEase(ease.inOut),
      },
    },
  });

  // -------------------------------------------------------------------------
  // Component overrides — applied after creation so they can read `theme`.
  // -------------------------------------------------------------------------
  theme.components = {
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        body: { backgroundColor: surface.canvas, color: text.primary },
        // Anything animated stops moving when the OS asks for reduced motion.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        '::selection': { backgroundColor: alpha(accent[500], 0.22) },
        code: { fontFamily: fontFamilyMono },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          minHeight: controlHeight,
          padding: '6px 16px',
          transition: `background-color ${ms(duration.fast)} ${cssEase(ease.out)}, box-shadow ${ms(
            duration.fast,
          )} ${cssEase(ease.out)}, transform ${ms(duration.fast)} ${cssEase(ease.out)}, border-color ${ms(
            duration.fast,
          )} ${cssEase(ease.out)}`,
          '&:active': { transform: 'translateY(1px)' },
        },
        sizeLarge: { minHeight: 0, padding: '13px 26px', fontSize: '1rem' },
        sizeSmall: { minHeight: 0, padding: '6px 12px', fontSize: '0.875rem' },
        contained: {
          boxShadow: shadow.xs,
          '&:hover': { boxShadow: shadow.sm },
        },
        outlined: {
          borderColor: surface.borderStrong,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, isLight ? 0.04 : 0.12),
          },
        },
        text: {
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, isLight ? 0.06 : 0.14) },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          transition: `background-color ${ms(duration.fast)} ${cssEase(ease.out)}, color ${ms(
            duration.fast,
          )} ${cssEase(ease.out)}`,
        },
        sizeMedium: { width: controlHeight, height: controlHeight },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: surface.border },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.card,
          border: `1px solid ${surface.border}`,
          backgroundColor: surface.paper,
          boxShadow: shadow.xs,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: alpha(surface.paper, isLight ? 0.82 : 0.72),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${surface.border}`,
          color: text.primary,
        },
      },
    },

    // MUI's `small` input is the 40px control, so it is the default everywhere
    // — including fields outside a TextField and the date pickers' own field.
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiPickersTextField: { defaultProps: { size: 'small' } },
    MuiPickersOutlinedInput: { styleOverrides: { root: { borderRadius: radius.control } } },

    MuiOutlinedInput: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          backgroundColor: isLight ? grey[0] : alpha(grey[800], 0.5),
          transition: `border-color ${ms(duration.fast)} ${cssEase(ease.out)}, box-shadow ${ms(
            duration.fast,
          )} ${cssEase(ease.out)}`,
          '& fieldset': { borderColor: surface.border },
          '&:hover fieldset': { borderColor: surface.borderStrong },
          '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, isLight ? 0.14 : 0.24)}`,
          },
          '&.Mui-focused fieldset': { borderWidth: 1, borderColor: theme.palette.primary.main },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.xs, fontWeight: 600, fontSize: '0.75rem' },
        sizeSmall: { height: 24 },
        outlined: { borderColor: surface.borderStrong },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: radius.card,
          border: `1px solid ${surface.border}`,
          backgroundColor: surface.paper,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: surface.sunken,
          '& .MuiTableCell-root': {
            fontSize: '0.6875rem',
            fontWeight: 650,
            letterSpacing: tracking.overline,
            textTransform: 'uppercase',
            color: text.secondary,
            borderBottom: `1px solid ${surface.border}`,
            whiteSpace: 'nowrap',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${surface.border}`, padding: '14px 16px' },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color ${ms(duration.fast)} ${cssEase(ease.out)}`,
          '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
        },
        hover: {
          '&:hover': { backgroundColor: isLight ? grey[50] : alpha(grey[800], 0.6) },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.lg,
          border: `1px solid ${surface.border}`,
          boxShadow: shadow['2xl'],
          backgroundImage: 'none',
        },
      },
    },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 650, padding: '20px 24px 8px' } } },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px 20px', gap: 8 } } },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isLight ? grey[900] : grey[700],
          fontSize: '0.8125rem',
          fontWeight: 500,
          borderRadius: radius.xs,
          padding: '6px 10px',
        },
        arrow: { color: isLight ? grey[900] : grey[700] },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.sm, border: '1px solid transparent', fontSize: '0.9375rem' },
        // Tint + strong text from the semantic tokens; MUI's own derivation
        // (lighten/darken of `light`) reads muddy against mode-aware tints.
        standardError: { backgroundColor: sem.danger.light, color: sem.danger.dark, borderColor: alpha(sem.danger.main, 0.28) },
        standardSuccess: { backgroundColor: sem.success.light, color: sem.success.dark, borderColor: alpha(sem.success.main, 0.28) },
        standardWarning: { backgroundColor: sem.warning.light, color: sem.warning.dark, borderColor: alpha(sem.warning.main, 0.28) },
        standardInfo: { backgroundColor: sem.info.light, color: sem.info.dark, borderColor: alpha(sem.info.main, 0.28) },
      },
    },

    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { borderRadius: radius.xs, backgroundColor: isLight ? grey[100] : grey[800] },
      },
    },

    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { fontWeight: 550 } },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: surface.border } } },

    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem', minHeight: 44 },
      },
    },

    MuiContainer: { defaultProps: { maxWidth: 'lg' } },
  };

  return theme;
};

/**
 * Accent-coloured text sitting on the page canvas rather than a card — the
 * small uppercase eyebrows above page and section titles. In light mode
 * primary.main (accent.600) is 4.17:1 on grey.50, under the 4.5:1 AA minimum
 * for text that size, so it steps to primary.dark (accent.700, 6.07:1). Dark
 * mode's primary.main already clears it (8.17:1). Use as an sx colour:
 * `sx={{ color: canvasAccentText }}`.
 */
export const canvasAccentText = (theme: Theme) =>
  theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.main;

/** Surface colours some components need directly (e.g. gradients, overlays). */
export const getSurfaces = (theme: Theme) => ({
  canvas: theme.palette.background.default,
  paper: theme.palette.background.paper,
  border: theme.palette.divider,
});
